import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types

/** A reusable canned response for the reply inbox (FIR-7582). */
export interface ReplyTemplate {
  id: number
  name: string
  description: string
  /** Quill HTML; may contain {VARIABLE} merge tokens resolved at insert time. */
  body: string
  created_at: string
  updated_at: string
}

export interface SaveReplyTemplatePayload {
  name: string
  description?: string
  body?: string
}

// Query keys
export const replyTemplateKeys = {
  all: ['reply-templates'] as const,
  list: () => [...replyTemplateKeys.all, 'list'] as const,
}

// API calls
const listReplyTemplates = async (): Promise<ReplyTemplate[]> => {
  // Read query — fail quietly (no error toast); the drawer renders an
  // empty/loading state instead. Avoids a scary toast if the endpoint isn't
  // available yet or returns nothing.
  return baseAPICall<ReplyTemplate[]>(
    () => clientInstance.get('/emailbison/reply-templates/'),
    { silentStatuses: [404] }
  )
}

const saveReplyTemplate = async (
  payload: SaveReplyTemplatePayload
): Promise<ReplyTemplate> => {
  return baseAPICall<ReplyTemplate>(() =>
    clientInstance.post('/emailbison/reply-templates/', payload)
  )
}

const deleteReplyTemplate = async (id: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/reply-templates/${id}/`)
  )
}

// Hooks
export const useReplyTemplates = (enabled = true) => {
  return useQuery({
    queryKey: replyTemplateKeys.list(),
    queryFn: listReplyTemplates,
    enabled,
  })
}

export const useSaveReplyTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveReplyTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: replyTemplateKeys.list() })
    },
  })
}

export const useDeleteReplyTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteReplyTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: replyTemplateKeys.list() })
    },
  })
}
